import { DOCUMENT } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  Renderer2,
  Inject,
} from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { BehaviorSubject } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { SIDEBAR_ANIMATION_DELAY } from './constants';

/**
 * The sidebar component contains only the design of the sidebar and the open/close mechanism.
 *
 * The content is displayed thanks to the `ng-content` tags. The `open()` and `close()` functions
 * are accessible and can be called through a component reference as in the example above with the
 * `#sideBar` variable.
 *
 * By default, the z-index is the same as the one used for the modal.
 *
 * The sidebar will be displayed on the left of the page and will be automatically closed after
 * clicking on the * overlay thanks to the `fcomFocusOut` directive.
 *
 * @example
 * <button (click)="sideBar.open()">Open sideBar</button>
 * <fcom-sidebar #sideBar>
 *   <header>
 *     <h2 class="font-heading-3 flex-1 truncate overflow-hidden nordic-blue-900-text">
 *       TRIP
 *     </h2>
 *     <button type="button" (click)="sideBar.close()">
 *       <fcom-icon class="ir-xxsmall" name="close-delete"></fcom-icon>
 *     </button>
 *   </header>
 * </fcom-sidebar>
 */
@Component({
  selector: 'fcom-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  animations: [
    trigger('sliderInOut', [
      state('in', style({ transform: 'translateX(0)' })),
      transition('void => *', [style({ transform: 'translateX(100%)' }), animate(SIDEBAR_ANIMATION_DELAY)]),
      transition('* => void', [animate(SIDEBAR_ANIMATION_DELAY, style({ transform: 'translateX(100%)' }))]),
    ]),
    trigger('overlayInOut', [
      state(
        'void',
        style({
          opacity: 0,
        })
      ),
      state(
        '*',
        style({
          opacity: 1,
        })
      ),
      transition('void => *', [animate(SIDEBAR_ANIMATION_DELAY)]),
      transition('* => void', [animate(SIDEBAR_ANIMATION_DELAY)]),
    ]),
  ],
})
export class SideBarComponent implements OnChanges {
  public readonly sidebarId = uuid();
  public readonly sidebarTitleId = `sidebar-${this.sidebarId}-title`;
  public readonly sidebarDescriptionId = `sidebar-${this.sidebarId}-description`;

  @ViewChild('sideBarHeading')
  sideBarHeading: ElementRef;

  /**
   * Whether the sidebar is open or closed.
   */
  @Input()
  isOpen = false;

  /**
   * The title to use in the header of the sidebar.
   */
  @Input()
  title: string;

  /**
   * The aria tag for the close icon button.
   */
  @Input()
  ariaClose = 'Close';

  /**
   * @Deprecated
   * Replacement can be found in the "close" output.
   */
  @Output()
  sideBarClosed: EventEmitter<boolean> = new EventEmitter();

  /**
   * Emits an event when the sidebar is closed
   */
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output()
  close: EventEmitter<void> = new EventEmitter();

  showSideBar$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly renderer: Renderer2
  ) {}

  ngOnChanges(changes): void {
    if (changes.isOpen) {
      if (changes.isOpen.currentValue) {
        this.open();
      } else if (changes.isOpen.previousValue !== undefined) {
        this.onClose();
      }
    }
  }

  open(): void {
    this.isOpen = true;
    this.showSideBar$.next(true);
    this.renderer.addClass(this.document.body, 'modal-open');
    this.changeDetectorRef.detectChanges();
    this.sideBarHeading.nativeElement.focus();
  }

  onClose(): void {
    this.isOpen = false;
    this.showSideBar$.next(false);
    this.sideBarClosed.emit(true);
    this.close.emit();
    this.renderer.removeClass(this.document.body, 'modal-open');
    this.changeDetectorRef.detectChanges();
  }
}
